'use client';

import React, { useState } from 'react';
// No Metadata import needed here

// Component name changed to reflect it's the page content
export default function ContactFormPageContent() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'submitting' | 'success' | 'error' | 'validationError'>('idle');
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
        setSubmissionStatus('validationError');
        setFeedbackMessage('Please fill in all required fields.');
        return;
    }
    setSubmissionStatus('submitting');
    setFeedbackMessage('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = (await response.json()) as { message?: string };

      if (response.ok) {
        setSubmissionStatus('success');
        setFeedbackMessage(result.message || 'Message sent successfully!');
        setFormData({ name: '', email: '', subject: '', message: '' }); // Reset form
      } else {
        setSubmissionStatus('error');
        setFeedbackMessage(result.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmissionStatus('error');
      setFeedbackMessage('An unexpected error occurred. Please try again.');
      console.error('Contact form submission error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-[#59b9c1] to-[#59b9c1]">
          Contact Us
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-100 mb-6">Get in Touch</h2>
            <p className="text-gray-300 mb-6">
              Have questions about our products, pricing, or anything else? Our team is ready to
              answer all your questions.
            </p>

            <div className="space-y-4 text-gray-300">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-3 text-[#59b9c1]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <a
                  href="mailto:info@datajukebox.com"
                  className="text-[#59b9c1] hover:text-[#7ad3d9]"
                >
                  info@datajukebox.com
                </a>
              </div>

              <div className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 text-[#59b9c1] mt-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Sydney, Australia</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-100 mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name} onChange={handleChange}
                  autoComplete="name"
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-gray-100 focus:outline-none focus:ring-[#59b9c1] focus:border-[#59b9c1] sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email} onChange={handleChange}
                  autoComplete="email"
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-gray-100 focus:outline-none focus:ring-[#59b9c1] focus:border-[#59b9c1] sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  id="subject"
                  value={formData.subject} onChange={handleChange}
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-gray-100 focus:outline-none focus:ring-[#59b9c1] focus:border-[#59b9c1] sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message} onChange={handleChange}
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-gray-100 focus:outline-none focus:ring-[#59b9c1] focus:border-[#59b9c1] sm:text-sm"
                ></textarea>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={submissionStatus === 'submitting'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#59b9c1]/75 transition-all duration-200 ease-out [&:hover]:bg-[#59b9c1] [&:hover]:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#59b9c1] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submissionStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </div>
              {feedbackMessage && (
                <div className={`mt-4 text-sm text-center ${submissionStatus === 'success' ? 'text-green-400' : submissionStatus === 'error' || submissionStatus === 'validationError' ? 'text-red-400' : 'text-gray-300'}`}>
                  {feedbackMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 